// src/modules/app-views/customers/components/CustomersPreviewHeader.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Divider, Tag, Typography, theme } from 'antd';
import SimpleAvatar from 'components/SimpleComponents/SimpleAvatar';
import translateX from 'utils/translateX';
import useZustand from 'hooks/useZustand';
import app from 'configs/ConfigIndex';
import { parseCpfToDisplay, parseCnpjToDisplay, returnOnlyNumbers } from 'utils/helpers';

const { Text, Title } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const AVATAR_SIZE = 100;
const BG_IMAGE_PATH = '/img/others/bg-applekkus.jpg';

const HEADER_AREA_STYLE = { position: 'relative', height: 140 };
const AVATAR_WRAPPER_STYLE = {
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10,
};
const AVATAR_INNER_STYLE_BASE = {
  width: AVATAR_SIZE,
  height: AVATAR_SIZE,
  borderRadius: 8,
  overflow: 'hidden',
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
};
const CONTENT_STYLE = { textAlign: 'center' };
const TAGS_ROW_STYLE = { marginBottom: 12, display: 'flex', justifyContent: 'center' };
const DIVIDER_TOP_STYLE = { margin: '16px 0' };
const TITLE_STYLE = { marginBottom: 4 };
const SECONDARY_TEXT_STYLE = { fontSize: 13 };
const DIVIDER_BOTTOM_STYLE = { margin: '24px 0 16px' };

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const CustomersPreviewHeader = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { selectedDocument: r } = props;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const { token } = theme.useToken();
  const currentTheme = useZustand('theme', 'currentTheme');
  const isDarkMode = currentTheme === 'dark';
  const baseColor = app?.baseColor || token.colorPrimary;

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  if (!r) return null;

  const isCompany = r?.entityType === 'company' || !!r?.cnpj?.value;
  const displayName = r?.name || r?.fullName || '—';

  const cpfValue = r?.cpf?.value;
  const cnpjValue = r?.cnpj?.value;
  let docNumber = null;
  if (isCompany && cnpjValue) {
    docNumber = `CNPJ: ${parseCnpjToDisplay(returnOnlyNumbers(cnpjValue))}`;
  } else if (!isCompany && cpfValue) {
    docNumber = `CPF: ${parseCpfToDisplay(returnOnlyNumbers(cpfValue))}`;
  }

  const location =
    r?.address?.__city ||
    (r?.address?.city && r?.address?.region
      ? `${r.address.city} / ${r.address.region}`
      : r?.address?.city);

  const avatarSrc =
    r?.avatar?.base64Thumb || r?.avatar?.storageUrl || r?.avatar?.thumbUrl;

  // Dynamic styles computed from runtime values
  const bgStyle = {
    position: 'absolute',
    top: -24,
    left: -24,
    right: -24,
    height: 80,
    backgroundColor: baseColor,
    backgroundImage: `url(${BG_IMAGE_PATH})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center bottom',
    backgroundRepeat: 'no-repeat',
  };
  const avatarInnerStyle = {
    ...AVATAR_INNER_STYLE_BASE,
    border: `1px solid ${isDarkMode ? token.colorBorderSecondary : token.colorBorder}`,
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <div>
      {/* Background Header Area */}
      <div style={HEADER_AREA_STYLE}>
        <div style={bgStyle} />

        {/* Customer Avatar — centered, overlapping background */}
        <div style={AVATAR_WRAPPER_STYLE}>
          <div style={avatarInnerStyle}>
            <SimpleAvatar
              src={avatarSrc}
              name={displayName}
              size={AVATAR_SIZE}
              shape="square"
              showShadow={false}
            />
          </div>
        </div>
      </div>

      {/* Content below avatar */}
      <div style={CONTENT_STYLE}>
        <div style={TAGS_ROW_STYLE}>
          <Tag color={isCompany ? 'blue' : 'green'}>
            {isCompany ? translateX('legal_person') : translateX('individual_person')}
          </Tag>
        </div>

        <Divider style={DIVIDER_TOP_STYLE} />

        <Title level={4} style={TITLE_STYLE}>
          {displayName}
        </Title>

        {docNumber && (
          <Text type="secondary" style={SECONDARY_TEXT_STYLE}>
            {docNumber}
          </Text>
        )}

        {location && (
          <>
            <br />
            <Text type="secondary" style={SECONDARY_TEXT_STYLE}>
              {location}
            </Text>
          </>
        )}

        <Divider style={DIVIDER_BOTTOM_STYLE} />
      </div>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default CustomersPreviewHeader;
